import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../components/Button';
import Input from '../components/Input';
import Card from '../components/Card';
import { useGameStore } from '../stores/gameStore';
import { gameService } from '../services/gameService';
import { useSocket } from '../context/SocketContext';
import {
	CATEGORY_VALUES,
	DIFFICULTY_VALUES,
	AMOUNT_OPTIONS_VALUES,
	TIME_PER_QUESTION_OPTIONS_VALUES,
} from '../utils/gameOptions';

const CreateGame = () => {
	const { t, i18n } = useTranslation();
	const { socket, connected } = useSocket();

	const error = useGameStore((state) => state.error);
	const setError = useGameStore((state) => state.setError);
	const setGameHostName = useGameStore((state) => state.setPlayerName);
	const playerName = useGameStore((state) => state.playerName);
	const playerId = useGameStore((state) => state.playerId);
	const setNavigationTarget = useGameStore((state) => state.setNavigationTarget);

	const currentLang = i18n.language.split('-')[0].toLowerCase();

	const [settings, setSettings] = useState({
		category: 'any',
		difficulty: 'any',
		amount: 10,
		timePerQuestion: 30,
		language: currentLang || 'en',
	});

	useEffect(() => {
		setSettings((prev) => ({ ...prev, language: currentLang }));
	}, [currentLang]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		const newSettings = {
			...settings,
			[name]: name === 'amount' || name === 'timePerQuestion' ? parseInt(value) : value,
		};
		setSettings(newSettings);

		if (error) setError('');
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!playerName.trim()) {
			setError(t('common.pleaseEnterNameError'));
			return;
		}
		setGameHostName(playerName);
		gameService.createGame(socket, connected, { hostName: playerName, settings, playerId });
	};

	return (
		<div className='container-game flex flex-col items-center justify-center min-h-screen py-12'>
			<div className='text-center mb-8 animate-fade-in'>
				<h1 className='text-4xl font-bold text-white mb-2'>{t('createGameScreen.title')}</h1>
				<p className='text-white opacity-90'>{t('createGameScreen.configureMessage')}</p>
			</div>

			<Card className='w-full max-w-md animate-slide-up'>
				<form onSubmit={handleSubmit}>
					<Input
						label={t('common.yourNameLabel')}
						value={playerName}
						onChange={(e) => setGameHostName(e.target.value.toUpperCase())}
						placeholder={t('common.enterYourNamePlaceholder')}
						required
						autoFocus
						maxLength={15}
					/>
					<div className='mb-4'>
						<label className='label'>{t('common.categoryLabel')}</label>
						<select name='category' value={settings.category} onChange={handleChange} className='input w-full'>
							{CATEGORY_VALUES.map((value) => (
								<option key={value} value={value}>
									{t(`categories.${value}`)}
								</option>
							))}
						</select>
					</div>

					<div className='mb-4'>
						<label className='label'>{t('common.difficultyLabel')}</label>
						<select
							name='difficulty'
							value={settings.difficulty}
							onChange={handleChange}
							className='input w-full'
						>
							{DIFFICULTY_VALUES.map((value) => (
								<option key={value} value={value}>
									{t(`difficulties.${value}`)}
								</option>
							))}
						</select>
					</div>

					<div className='mb-4'>
						<label className='label'>{t('createGameScreen.numQuestionsLabel')}</label>
						<select name='amount' value={settings.amount} onChange={handleChange} className='input w-full'>
							{AMOUNT_OPTIONS_VALUES.map((amount) => (
								<option key={amount} value={amount}>
									{t('createGameScreen.numQuestions', { count: amount })}
								</option>
							))}
						</select>
					</div>

					<div className='mb-4'>
						<label className='label'>{t('createGameScreen.timePerQuestionLabel')}</label>
						<select
							name='timePerQuestion'
							value={settings.timePerQuestion}
							onChange={handleChange}
							className='input w-full'
						>
							{TIME_PER_QUESTION_OPTIONS_VALUES.map((time) => (
								<option key={time} value={time}>
									{t('createGameScreen.timeSeconds', { count: time })}
								</option>
							))}
						</select>
					</div>

					{error && <div className='mb-4 p-3 bg-danger-100 text-danger-700 rounded-lg'>{error}</div>}

					<div className='flex gap-3 mt-6'>
						<Button type='button' variant='secondary' onClick={() => setNavigationTarget('/')} className='flex-1'>
							{t('common.cancelButton')}
						</Button>
						<Button type='submit' variant='primary' className='flex-1'>
							{t('createGameScreen.createQuizButton')}
						</Button>
					</div>
				</form>
			</Card>
		</div>
	);
};

export default CreateGame;
